import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import { FormProvider, Controller, useForm } from "react-hook-form";

import clsx from "clsx";

import get from "lodash/get";
import map from "lodash/map";
import { AnimatePresence, motion } from "framer-motion";
import useClickAway from "react-use/lib/useClickAway";

import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Autocomplete from "@mui/material/Autocomplete";

import useIdleEffect from "../../lib/hooks/useIdleEffect";

import Title from "../../components/Title";
import Modal from "../../components/Modal";
import BackButton from "../../components/BackButton";
import VirtualKeyboard, {
  LayoutType,
  VirtualKeyboardRef,
} from "../../components/VirtualKeyboard";
import Curve from "../../components/Animations/PageTransitions/Curve";

import { client as graphqlCLient } from "../../bit/board/hooks/useGraphQLClient";

import { useDebounceStatus } from "../../lib/hooks/useDebounceStatus";
import { useTimeoutFunction } from "../../lib/hooks/useTimeoutFunction";

import { MUTATION_CHECKOUT_OWNER } from "../../config/graphql/mutation";
import { QUERY_CURRENTLY_CHECKED_IN } from "../../config/graphql/query";
import {
  useIdentificationCode,
  useLayout,
} from "../../lib/hooks/useQueryBoard";
import OfflineModal from "../../components/Offline/OfflineModal";
import MoveLeft from "../../components/Animations/MoveLeft";

interface FieldValues {
  name: any;
}

const ManualCheckout = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [layout, setLayout] = useState<LayoutType>("default");
  const [focusedInput, setFocusedInput] = useState<string | undefined>("name");

  const identificationCode = useIdentificationCode();

  const keyboard = useRef<VirtualKeyboardRef>(null);
  const keyboardBox = useRef<HTMLDivElement>(null);

  const [nameForList, setNameForList] = useState<string>("");
  const [checkoutList, setCheckoutList] = useState<
    {
      id: string;
      label: string;
    }[]
  >([]);

  const { checkout } = useLayout();

  const methods = useForm<FieldValues>({
    defaultValues: {
      name: undefined,
    },
    mode: "onSubmit",
  });

  useIdleEffect(() => {
    navigate("/", { replace: true });
  }, 60000);

  const { isLoading } = useQuery<{
    currentlyCheckedIn: { events: IAccessEvent[] };
  }>(
    [
      "currentlyCheckedIn",
      {
        filter: {
          owner: {
            fullName: nameForList,
            guestMatch: {
              code: identificationCode,
              value: nameForList,
            },
          },
        },
      },
    ],
    ({ queryKey: [_key, variables] }: any) =>
      graphqlCLient.request<{
        currentlyCheckedIn: { events: IAccessEvent[] };
      }>(QUERY_CURRENTLY_CHECKED_IN, variables),
    {
      enabled: nameForList.length === 2,
      onSuccess: (data) => {
        const list = map(data?.currentlyCheckedIn?.events, (accessEvent) => {
          const owner = get(accessEvent, "owner");

          let label = "";

          if ("fullName" in owner) {
            label = owner.fullName;
          } else if ("fields" in owner) {
            label =
              owner.fields.find(
                (field: { name: string }) =>
                  field.name === "fullName" ||
                  field.name === identificationCode,
              )?.value || "";
          }
          return {
            label,
            id: accessEvent.owner.id,
          };
        });
        setCheckoutList(list);
      },
    },
  );

  useEffect(() => {
    if (nameForList.length < 3) {
      setCheckoutList([]);
    }
  }, [nameForList]);

  const {
    data,
    status: requestStatus,
    mutate: onCheckoutOwner,
    reset,
  } = useMutation(async (id: string) => {
    return graphqlCLient.request<
      { checkOutOwner: IAccessEvent },
      { id: string }
    >(MUTATION_CHECKOUT_OWNER, {
      id,
    });
  });

  const status = useDebounceStatus(requestStatus, 1500);

  const onDismiss = useCallback(() => {
    if (status === "success") {
      return navigate("/", { replace: true });
    }

    if (status === "error") {
      return reset();
    }

    return navigate("/", { replace: true });
  }, [status, data]);

  const timeout = useMemo(() => {
    if (status === "error") {
      return 10000;
    }

    if (status === "success") {
      return 3000;
    }

    return 0;
  }, [status]);

  useTimeoutFunction(onDismiss, timeout);

  const getLayoutForFieldAndValue = useCallback(
    (layout: LayoutType = "default", _field = "", value = "") => {
      if (layout === "default-alt") {
        return layout;
      }

      // Capitalize after whitespace and at start
      if (!value || value.slice(-1) === " ") {
        return "default-shift";
      }

      return "default";
    },
    [],
  );

  const onKeyboardChange = useCallback(
    (value = "") => {
      if (value.length === 2) {
        setNameForList(value);
      }

      if (value.length === 1) {
        setNameForList("");
      }

      const input = document.querySelector<HTMLInputElement>(
        `[name="${focusedInput}"]`,
      );
      let selectionStart = Math.max(0, value.length - 1);

      if (input && typeof input.selectionStart === "number") {
        selectionStart = input.selectionStart;
      }

      if (!focusedInput) {
        return;
      }

      let prevValue = methods.getValues(focusedInput as any);

      setLayout((prevLayout) => {
        const nextLayout = getLayoutForFieldAndValue(
          prevLayout,
          focusedInput,
          value,
        );

        return nextLayout;
      });

      if (focusedInput === "name") {
        if (prevValue && typeof prevValue.label === "string") {
          prevValue = prevValue.label;
        }

        methods.setValue("name", { label: value });
      } else {
        methods.setValue(focusedInput as any, value);
      }

      if (!prevValue) {
        return;
      }

      /**
       * Update caret position of text input
       */
      if (input) {
        const position = prevValue.length - value.length;

        const next = Math.max(0, selectionStart - position);

        input.setSelectionRange(next, next);
      }
    },
    [focusedInput],
  );

  const onKeyboardKeyPress = useCallback(
    (button = "") => {
      if (button === "{alt}") {
        setLayout(
          (layout) =>
            (layout.endsWith("alt")
              ? layout.split("-").shift()
              : `${layout.split("-").shift()}-alt`) as LayoutType,
        );
      }

      if (button === "{shift}") {
        setLayout(
          (layout) =>
            (layout.endsWith("shift")
              ? layout.split("-").shift()
              : `${layout.split("-").shift()}-shift`) as LayoutType,
        );
      }
    },
    [focusedInput, layout],
  );

  useClickAway(keyboardBox, (event) => {
    if (event.target && (event.target as HTMLElement)?.tagName === "INPUT") {
      return;
    }

    setFocusedInput(undefined);
  });

  const onSubmit = useCallback((variables: FieldValues) => {
    return onCheckoutOwner(variables.name.id);
  }, []);

  /**
   * Synchronize text caret position with virtual keyboard
   */
  const onInputSelect = useCallback(
    (event: React.SyntheticEvent<HTMLInputElement, Event>) => {
      if (!keyboard.current) {
        return;
      }

      const { selectionStart, selectionEnd } = event.target as HTMLInputElement;

      if (
        !(
          typeof selectionStart === "number" && typeof selectionEnd === "number"
        )
      ) {
        return;
      }

      keyboard.current.setCaretPosition(selectionStart, selectionEnd);
    },
    [],
  );

  const onInputFocus = useCallback(
    (
      event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>,
    ) => {
      const {
        target: { name, value },
      } = event;

      /**
       * Skip if there is no virtual keyboard
       */
      if (!keyboard.current) {
        return;
      }

      setLayout((prevLayout) =>
        getLayoutForFieldAndValue(prevLayout, name, value),
      );

      setFocusedInput(name);
    },
    [],
  );

  /**
   * Synchronize text input with virtual keyboard
   */
  const onInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const {
        target: { name, value, selectionStart, selectionEnd },
      } = event;

      /**
       * Skip if there is no virtual keyboard
       */
      if (!keyboard.current) {
        return;
      }

      setLayout((prevLayout) =>
        getLayoutForFieldAndValue(prevLayout, name, value),
      );

      keyboard.current.setInput(value, name);
      keyboard.current.setCaretPosition(selectionStart, selectionEnd);
    },
    [],
  );

  return (
    <Curve>
      <FormProvider {...methods}>
        <div className="d-flex align-items-center flex-column">
          <BackButton
            to={checkout?.enabled ? "/access/option/checkout" : "/"}
          />
          <Title className="page-title">
            <Typography variant="h1">
              <Trans i18nKey="manualCheckout:title" />
            </Typography>
          </Title>
          <form
            className="form d-flex flex-column"
            autoComplete="off"
            onSubmit={methods.handleSubmit(onSubmit)}
          >
            <MoveLeft delay={0.55}>
              <motion.div className="search-input">
                <Controller
                  name="name"
                  rules={{
                    required: {
                      value: true,
                      message: t("manualCheckout:error.required"),
                    },
                    validate: {
                      exists: (value: any) => {
                        if (!(value && value.id)) {
                          return t("manualCheckout:error.required");
                        }

                        return true;
                      },
                    },
                  }}
                  render={({
                    field: { onChange, name, value },
                    fieldState: { error },
                  }) => {
                    return (
                      <Autocomplete
                        clearOnBlur
                        blurOnSelect
                        disableClearable
                        loading={isLoading}
                        loadingText={t("manualCheckout:form.loading")}
                        noOptionsText={t("manualCheckout:form.noOptions")}
                        open={nameForList.length > 0}
                        onClose={() => {
                          setNameForList("");
                        }}
                        value={!!(value && value.id) ? value : null}
                        options={checkoutList}
                        getOptionLabel={(option) => option.label || ""}
                        isOptionEqualToValue={(option, value) =>
                          value?.id === option?.id
                        }
                        renderOption={(props, option) => (
                          <li {...props} key={option.id}>
                            {option.label}
                          </li>
                        )}
                        onChange={(_event, newValue, reason) => {
                          if (reason === "selectOption") {
                            onChange(newValue);
                          }
                        }}
                        onBlur={() => {
                          const currentValue = methods.getValues(name);

                          if (!!(currentValue && currentValue.id)) {
                            return;
                          }

                          if (keyboard.current) {
                            keyboard.current.setInput("", name);
                          }

                          onChange(undefined);
                        }}
                        inputValue={!!(value && value.label) ? value.label : ""}
                        renderInput={(params) => {
                          return (
                            <TextField
                              {...params}
                              type="text"
                              name="name"
                              variant="outlined"
                              autoFocus
                              error={!!error}
                              helperText={error?.message ?? " "}
                              label={t("manualCheckout:form.placeholder.name")}
                              onChange={(event) => {
                                const {
                                  target: { value },
                                } = event;
                                if (value.length === 2) {
                                  setNameForList(value);
                                }

                                if (value.length === 1) {
                                  setNameForList("");
                                }

                                onInputChange(event);

                                onChange({
                                  label: value,
                                });
                              }}
                              onFocus={onInputFocus}
                              onSelect={onInputSelect}
                            />
                          );
                        }}
                      />
                    );
                  }}
                />
              </motion.div>
            </MoveLeft>
            <MoveLeft delay={0.65}>
              <motion.div className="w-100">
                <Button
                  variant="contained"
                  onClick={methods.handleSubmit(onSubmit)}
                  className="mt-4 w-100"
                >
                  {t("manualCheckout:btn")}
                </Button>
              </motion.div>
            </MoveLeft>
          </form>
          <AnimatePresence mode="wait">
            {status !== "idle" && (
              <Modal
                title={
                  <Trans i18nKey={`manualCheckout:status.${status}.title`} />
                }
                message={
                  <Trans i18nKey={`manualCheckout:status.${status}.message`} />
                }
                status={status}
                onDismiss={onDismiss}
              />
            )}
          </AnimatePresence>

          <div
            ref={keyboardBox}
            className={clsx("keyboard ", { keyboard__out: !focusedInput })}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <VirtualKeyboard
              ref={keyboard}
              layoutName={layout}
              inputName={focusedInput}
              onChange={onKeyboardChange}
              onKeyPress={onKeyboardKeyPress}
            />
          </div>
        </div>
      </FormProvider>

      <OfflineModal />
    </Curve>
  );
};

export default ManualCheckout;
